import { useEffect, useRef } from 'react';
import useHashFlag from '../../../../hooks/useHashFlag';
import HASH_FLAGS from '../../../../routes/hashFlags.const';
import { useProductDetailsProductColor } from '../../../../services/modules/productDetails/productDetails.queries';
import { useOrderBookStoreIsHydrated } from '../../../../stores/OrderBook/hooks/useOrderBookStoreIsHydrated';
import useProductDetailsStore from '../../../../stores/ProductDetails/productDetails.store';
import { selectIsProductDetailsOpen } from '../../../../stores/ProductDetails/productDetails.store.selectors';
import useIsAdminSeller from '../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import useOpenProductDetails from './useOpenProductDetails';
import useOpenProductDetailsFromFragment from './useOpenProductDetailsFromFragment';
import useProductDetailsFragmentMirror from './useProductDetailsFragmentMirror';
import {
  ProductDetailsFragment,
  isProductDetailsInSyncWithFragment,
  resolveFragment,
  resolveOpenedItem,
  resolveProductDetailsHashSyncAction,
} from './useProductDetailsHashSync.utils';

interface UseProductDetailsHashSyncParams {
  onCloseModal: () => void;
}

const useProductDetailsHashSync = ({ onCloseModal }: UseProductDetailsHashSyncParams) => {
  const isAdminSeller = useIsAdminSeller();
  const isOrderBookHydrated = useOrderBookStoreIsHydrated();
  const { openProductDetails } = useOpenProductDetails();

  const isModalOpen = useProductDetailsStore(selectIsProductDetailsOpen);
  const openedProductColorId = useProductDetailsStore(
    (state) => state.productDetailsModalState.productColorId,
  );
  const openedKitId = useProductDetailsStore((state) => state.productDetailsModalState.kitId);

  const productFragment = useHashFlag(HASH_FLAGS.productDetails);
  const kitFragment = useHashFlag(HASH_FLAGS.kitDetails);

  const fragment = resolveFragment({
    isProductFragmentOpen: productFragment.isOpen,
    productFragmentValue: productFragment.value,
    isKitFragmentOpen: kitFragment.isOpen,
    kitFragmentValue: kitFragment.value,
  });
  const openedItem = resolveOpenedItem({ isModalOpen, openedProductColorId, openedKitId });

  const lastSyncedEntityRef = useRef<ProductDetailsFragment>();

  const action = resolveProductDetailsHashSyncAction({
    fragment,
    openedItem,
    lastSyncedEntity: lastSyncedEntityRef.current,
  });
  const isInSync = isProductDetailsInSyncWithFragment({ fragment, openedItem });

  const shouldOpenFromFragment = action === 'openModalFromFragment' && isOrderBookHydrated;

  const { data: resolvedProductColor } = useProductDetailsProductColor(
    { productColorId: fragment?.value as string, isAdminSeller },
    { enabled: shouldOpenFromFragment && fragment?.kind === 'product' },
  );

  useEffect(() => {
    if (!isInSync) {
      return;
    }

    lastSyncedEntityRef.current = fragment;
  }, [isInSync, fragment]);

  useProductDetailsFragmentMirror({
    action,
    openedItemValue: openedItem?.value,
    onWriteFragment: openedItem?.kind === 'kit' ? kitFragment.open : productFragment.open,
    onDropFragment: fragment?.kind === 'kit' ? kitFragment.close : productFragment.close,
    onCloseModal,
  });

  useOpenProductDetailsFromFragment({
    isEnabled: shouldOpenFromFragment,
    fragmentKind: fragment?.kind,
    fragmentValue: fragment?.value,
    resolvedProductId: resolvedProductColor?.productId,
    onOpenProductDetails: openProductDetails,
  });
};

export default useProductDetailsHashSync;
